import { Identifiable } from '@approvalmax/types';
import { DropdownEditor, DropdownListItemProps, RadioGroupEditor } from '@approvalmax/ui';
import ListItem from '@approvalmax/ui/src/old/form/editors/dropdown/ListItem';
import { hooks } from '@approvalmax/utils';
import { BusinessSegment, PartnerSegment, TypeOfBusiness } from 'modules/data/domain';
import PlainDataProvider from 'modules/data-providers/dataProviders/PlainDataProvider';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { amplitudeService } from 'services/amplitude';

import {
    getInitialNumberOfClients,
    getInitialNumberOfEntities,
    getInitialPartnerStatus,
    getInitialTypeOfBusiness,
    getInitialuseCase,
    getInitiaProductInterest,
    largeBusinessByNumberOfEntities,
    largePartnerByStatus,
    numberOfClientsItems,
    numberOfEntitiesItems,
    partnerStatusItems,
    useCaseItems,
} from './QualificationQuestions.helpers';
import { useProductInterestItems } from './QualificationQuestions.hooks';
import { messages } from './QualificationQuestions.messages';
import {
    AnimatedSelectContainer,
    GreenCheckIcon,
    ListItemContent,
    ListItemText,
    ListItemWithIcon,
    Root,
    StyledRadioGroupEditor,
    Title,
} from './QualificationQuestions.styles';
import { ProductInterestItem, QualificationQuestionsProps } from './QualificationQuestions.types';

const QualificationQuestions: FC<QualificationQuestionsProps> = memo((props) => {
    const { initialData, onChange } = props;

    const [typeOfBusiness, setTypeOfBusiness] = useState<TypeOfBusiness | null>(getInitialTypeOfBusiness(initialData));
    const productInterestItems = useProductInterestItems();

    const handleChangeTypeOfBusiness = useCallback((typeOfBusiness: TypeOfBusiness) => {
        setTypeOfBusiness(typeOfBusiness);

        amplitudeService.sendData('signup: chose the type of company');
    }, []);

    const [partnerStatus, setPartnerStatus] = useState<(typeof partnerStatusItems)[number] | null>(
        getInitialPartnerStatus(initialData)
    );
    const [productInterest, setProductInterest] = useState<ProductInterestItem | null>(
        getInitiaProductInterest(initialData)
    );

    const [numberOfEntities, setNumberOfEntities] = useState<(typeof numberOfEntitiesItems)[number] | null>(
        getInitialNumberOfEntities(initialData)
    );
    const [numberOfClients, setNumberOfClients] = useState<(typeof numberOfClientsItems)[number] | null>(
        getInitialNumberOfClients(initialData)
    );

    const [useCase, setUseCase] = useState<typeof useCaseItems | null>(getInitialuseCase(initialData));

    useEffect(() => {
        if (typeOfBusiness === TypeOfBusiness.Partner) {
            onChange({
                typeOfBusiness: [typeOfBusiness],
                partnerStatus: partnerStatus ? [partnerStatus.id] : undefined,
                numberOfClients: numberOfClients ? [numberOfClients.id] : undefined,
                useCase: useCase?.map((el) => el.id),
                segment:
                    partnerStatus && largePartnerByStatus.has(partnerStatus.id)
                        ? [PartnerSegment.LargePartner]
                        : [PartnerSegment.SmallPartner],
            });
        } else if (typeOfBusiness === TypeOfBusiness.Business) {
            onChange({
                typeOfBusiness: [typeOfBusiness],
                productInterest: productInterest ? [productInterest.id] : undefined,
                numberOfEntities: numberOfEntities ? [numberOfEntities.id] : undefined,
                useCase: useCase?.map((el) => el.id),
                segment:
                    numberOfEntities && largeBusinessByNumberOfEntities.has(numberOfEntities.id)
                        ? [BusinessSegment.ManagedBusiness]
                        : [BusinessSegment.UnmanagedBusiness],
            });
        } else {
            onChange(null);
        }
    }, [numberOfClients, numberOfEntities, onChange, partnerStatus, productInterest, typeOfBusiness, useCase]);

    const listItem = useMemo(
        () => (props: DropdownListItemProps<Identifiable>) => (
            <ListItem {...props}>{(value) => <ListItemContent>{value}</ListItemContent>}</ListItem>
        ),
        []
    );

    const listItemMultiple = useMemo(
        () => (props: DropdownListItemProps<Identifiable>) => (
            <ListItem {...props}>
                {(value) => (
                    <ListItemWithIcon>
                        <ListItemText>{value}</ListItemText>

                        {props.isSelected && <GreenCheckIcon width={13} height={10} />}
                    </ListItemWithIcon>
                )}
            </ListItem>
        ),
        []
    );

    const { animatedStyles: partnerStatusStyles, animatedRef: partnerStatusRef } = hooks.useAnimatedHeight(
        typeOfBusiness === TypeOfBusiness.Partner
    );

    const { animatedStyles: numberOfClientsStyles, animatedRef: numberOfClientsRef } = hooks.useAnimatedHeight(
        typeOfBusiness === TypeOfBusiness.Partner && !!partnerStatus
    );

    const { animatedStyles: productInterestStyles, animatedRef: productInterestRef } = hooks.useAnimatedHeight(
        typeOfBusiness === TypeOfBusiness.Business
    );

    const { animatedStyles: numberOfEntitiesStyles, animatedRef: numberOfEntitiesRef } = hooks.useAnimatedHeight(
        typeOfBusiness === TypeOfBusiness.Business && !!productInterest
    );

    const { animatedStyles: useCaseStyles, animatedRef: useCaseRef } = hooks.useAnimatedHeight(
        Boolean(
            (typeOfBusiness === TypeOfBusiness.Partner && partnerStatus && numberOfClients) ||
                (typeOfBusiness === TypeOfBusiness.Business && productInterest && numberOfEntities)
        )
    );

    return (
        <Root>
            <Title>{messages.title}</Title>

            <StyledRadioGroupEditor value={typeOfBusiness} onChange={handleChangeTypeOfBusiness}>
                <RadioGroupEditor.Item id={TypeOfBusiness.Partner}>{messages.companyPartner}</RadioGroupEditor.Item>

                <RadioGroupEditor.Item id={TypeOfBusiness.Business}>{messages.companyBusiness}</RadioGroupEditor.Item>
            </StyledRadioGroupEditor>

            <AnimatedSelectContainer
                style={partnerStatusStyles}
                $withMargin={typeOfBusiness === TypeOfBusiness.Partner}
            >
                <div ref={partnerStatusRef}>
                    <PlainDataProvider items={partnerStatusItems}>
                        <DropdownEditor
                            value={partnerStatus}
                            onChange={(value) => setPartnerStatus(value as (typeof partnerStatusItems)[number])}
                            buttonComponent={DropdownEditor.NoFilterButton}
                            placeholder={messages.partnerStatusPlaceholder}
                            listItem={listItem}
                        />
                    </PlainDataProvider>
                </div>
            </AnimatedSelectContainer>

            <AnimatedSelectContainer
                style={numberOfClientsStyles}
                $withMargin={typeOfBusiness === TypeOfBusiness.Partner}
            >
                <div ref={numberOfClientsRef}>
                    <PlainDataProvider items={numberOfClientsItems}>
                        <DropdownEditor
                            value={numberOfClients}
                            onChange={(value) => setNumberOfClients(value as (typeof numberOfClientsItems)[number])}
                            buttonComponent={DropdownEditor.NoFilterButton}
                            placeholder={messages.numberOfClientsPlaceholder}
                            listItem={listItem}
                        />
                    </PlainDataProvider>
                </div>
            </AnimatedSelectContainer>

            <AnimatedSelectContainer
                style={productInterestStyles}
                $withMargin={typeOfBusiness === TypeOfBusiness.Business}
            >
                <div ref={productInterestRef}>
                    <PlainDataProvider items={productInterestItems}>
                        <DropdownEditor
                            value={productInterest}
                            onChange={(value) => setProductInterest(value as ProductInterestItem)}
                            buttonComponent={DropdownEditor.NoFilterButton}
                            placeholder={messages.productInterestPlaceholder}
                            listItem={listItem}
                        />
                    </PlainDataProvider>
                </div>
            </AnimatedSelectContainer>

            <AnimatedSelectContainer
                style={numberOfEntitiesStyles}
                $withMargin={typeOfBusiness === TypeOfBusiness.Business}
            >
                <div ref={numberOfEntitiesRef}>
                    <PlainDataProvider items={numberOfEntitiesItems}>
                        <DropdownEditor
                            value={numberOfEntities}
                            onChange={(value) => setNumberOfEntities(value as (typeof numberOfEntitiesItems)[number])}
                            buttonComponent={DropdownEditor.NoFilterButton}
                            placeholder={messages.numberOfEntitiesPlaceholder}
                            listItem={listItem}
                        />
                    </PlainDataProvider>
                </div>
            </AnimatedSelectContainer>

            <AnimatedSelectContainer style={useCaseStyles}>
                <div ref={useCaseRef}>
                    <PlainDataProvider items={useCaseItems}>
                        <DropdownEditor
                            value={useCase}
                            onChange={(value) => setUseCase(value as typeof useCaseItems)}
                            buttonComponent={DropdownEditor.NoFilterButton}
                            leaveSelectedInPanelWhenMultiple
                            noLimitHeightPanel
                            placeholder={messages.useCasePlaceholder}
                            buttonTextSelector={(value) => {
                                if (!value || !Array.isArray(value) || value.length === 0) {
                                    return '';
                                }

                                return messages.selectedCount({
                                    count: value.length,
                                });
                            }}
                            multiple
                            listItem={listItemMultiple}
                        />
                    </PlainDataProvider>
                </div>
            </AnimatedSelectContainer>
        </Root>
    );
});

export default QualificationQuestions;
